/** 管理画面の一覧用：開催日（event_date）範囲・キーワード・表サイト公開範囲の絞り込み */

import { GROUP_BADGE } from "../types/public";
import { matchesSearchQuery } from "../lib/home-quick-search";

export type AdminVisibilityFilter = "" | "unreleased" | "public";

export type AdminKarinSubtypeFilter = "all" | "mini_live" | "withlive" | "limista" | "live" | "m_line" | "other";

export const ADMIN_KARIN_SUBTYPE_FILTER_OPTIONS: { value: AdminKarinSubtypeFilter; label: string }[] = [
  { value: "all", label: "すべて" },
  { value: "mini_live", label: "ミニライブ＆一言" },
  { value: "withlive", label: "WithLIVE" },
  { value: "limista", label: "リミスタ" },
  { value: "live", label: "ライブ" },
  { value: "m_line", label: "M-line" },
  { value: "other", label: "その他" },
];

export function parseAdminKarinSubtypeFilter(raw: string | null | undefined): AdminKarinSubtypeFilter {
  const v = String(raw ?? "").trim();
  if (v === "mini_live" || v === "withlive" || v === "limista" || v === "live" || v === "m_line" || v === "other") return v;
  return "all";
}

/** カレンダーから一覧へスクロールするだけの日付（開催日絞り込みはしない） */
export function parseAdminFocusDay(raw: string | null | undefined): string | null {
  const d = String(raw ?? "").trim();
  return /^\d{4}-\d{2}-\d{2}$/.test(d) ? d : null;
}

export type AdminOutletFilter = {
  /** YYYY-MM-DD。空なら下限なし */
  adminEventDateFrom: string;
  /** YYYY-MM-DD。空なら上限なし */
  adminEventDateTo: string;
  adminKeyword: string;
  adminVis: AdminVisibilityFilter;
  /** 宮本佳林の予定タイプ（佳林行のみ。他は通過） */
  adminKarinSubtype: AdminKarinSubtypeFilter;
};

function asStr(v: unknown): string {
  return v == null ? "" : String(v);
}

function parsePerformersHaystack(performersJson: unknown): string {
  if (typeof performersJson !== "string") return "";
  try {
    const j = JSON.parse(performersJson) as unknown;
    return Array.isArray(j) ? j.map(String).join(" ") : "";
  } catch {
    return "";
  }
}

/** `events.event_date` の先頭10文字を YYYY-MM-DD として比較 */
export function adminEventDateRangeMatchesRow(dateFrom: string, dateTo: string, r: Record<string, unknown>): boolean {
  const from = dateFrom.trim();
  const to = dateTo.trim();
  if (!from && !to) return true;
  const raw = asStr(r.event_date).trim();
  const ed = /^\d{4}-\d{2}-\d{2}/.test(raw) ? raw.slice(0, 10) : "";
  if (!ed) return false;
  if (from && ed < from) return false;
  if (to && ed > to) return false;
  return true;
}

/** 表サイトと同じクイック検索・キーワード用の全文 */
export function adminRowSearchHaystack(r: Record<string, unknown>): string {
  const gc = asStr(r.group_code);
  const groupLabel = gc && GROUP_BADGE[gc] ? GROUP_BADGE[gc].label : "";
  return [
    asStr(r.event_name),
    asStr(r.venue),
    asStr(r.prefecture),
    asStr(r.performance_text),
    asStr(r.description),
    asStr(r.memo),
    asStr(r.detail_url),
    asStr(r.reserve_url),
    asStr(r.fan_group_name),
    asStr(r.fan_artist_name),
    asStr(r.source_title),
    asStr(r.fan_private_note),
    asStr(r.performance_count),
    asStr(r.event_date),
    asStr(r.time_start),
    asStr(r.time_end),
    asStr(r.homepage_public_date),
    asStr(r.homepage_public_at),
    asStr(r.source),
    asStr(r.status),
    asStr(r.visibility),
    groupLabel,
    gc,
    parsePerformersHaystack(r.performers_json),
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
}

export function adminKeywordMatchesRow(kw: string, r: Record<string, unknown>): boolean {
  return matchesSearchQuery(adminRowSearchHaystack(r), kw, asStr(r.group_code) || null);
}

/** 宮本佳林行のみ karin_subtype で絞る（表サイトと同じ） */
export function adminRowMatchesKarinSubtype(r: Record<string, unknown>, f: AdminKarinSubtypeFilter): boolean {
  if (!Number(r.is_karin)) return true;
  if (f === "all") return true;
  const sub = asStr(r.karin_subtype) || "other";
  return sub === f;
}

export function adminListFilterMatchesRow(dateFrom: string, dateTo: string, kw: string, r: Record<string, unknown>): boolean {
  return adminEventDateRangeMatchesRow(dateFrom, dateTo, r) && adminKeywordMatchesRow(kw, r);
}

/** 表サイト公開範囲（DB visibility）で絞り込み。空ならすべて通す */
export function adminVisibilityFilterMatchesRow(vis: AdminVisibilityFilter, r: Record<string, unknown>): boolean {
  if (!vis) return true;
  const k = asStr(r.visibility).trim().toLowerCase();
  if (vis === "unreleased") return k === "unreleased";
  if (vis === "public") return k === "public";
  return true;
}

export function adminListFilterMatchesRowWithVis(
  dateFrom: string,
  dateTo: string,
  kw: string,
  vis: AdminVisibilityFilter,
  r: Record<string, unknown>,
  karinSubtype: AdminKarinSubtypeFilter = "all",
): boolean {
  return (
    adminListFilterMatchesRow(dateFrom, dateTo, kw, r) &&
    adminVisibilityFilterMatchesRow(vis, r) &&
    adminRowMatchesKarinSubtype(r, karinSubtype)
  );
}

/** 予定一覧：開催日の絞り込みを付けていないときは 2026-05-01 以降だけ表示（アーカイブは除外） */
export const ADMIN_DEFAULT_MIN_EVENT_DATE = "2026-05-01";

export function adminRowPassesDefaultEventDateFloor(
  listMode: string | undefined,
  dateFrom: string,
  dateTo: string,
  r: Record<string, unknown>,
): boolean {
  if (listMode === "archive") return true;
  const df = dateFrom.trim();
  const dt = dateTo.trim();
  if (df || dt) return true;
  const raw = asStr(r.event_date).trim();
  const ed = /^\d{4}-\d{2}-\d{2}/.test(raw) ? raw.slice(0, 10) : "";
  if (!ed) return true;
  return ed >= ADMIN_DEFAULT_MIN_EVENT_DATE;
}

/** JST の「今月」を YYYY-MM で返す（予定管理のデフォルト月） */
export function jstCurrentYearMonth(now = new Date()): string {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Tokyo",
    year: "numeric",
    month: "2-digit",
  }).formatToParts(now);
  const y = parts.find((p) => p.type === "year")?.value;
  const m = parts.find((p) => p.type === "month")?.value;
  return `${y}-${m}`;
}

/** YYYY-MM からその月の末日 YYYY-MM-DD */
export function lastDayOfMonthYmd(ym: string): string {
  const m = ym.trim();
  if (!/^\d{4}-\d{2}$/.test(m)) return `${m}-31`;
  const [yStr, moStr] = m.split("-");
  const y = Number(yStr);
  const mo = Number(moStr);
  if (!y || !mo || mo < 1 || mo > 12) return `${m}-31`;
  const d = new Date(y, mo, 0).getDate();
  return `${yStr}-${moStr}-${String(d).padStart(2, "0")}`;
}

/** JST 基準で前後の月を並べる YYYY-MM 値（開催月だけの絞り込み用） */
export function buildAdminMonthChoices(pivot = new Date(), past = 4, future = 20): string[] {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Tokyo",
    year: "numeric",
    month: "2-digit",
  }).formatToParts(pivot);
  const y = Number(parts.find((p) => p.type === "year")?.value ?? "2000");
  const mo = Number(parts.find((p) => p.type === "month")?.value ?? "1");
  const out: string[] = [];
  for (let i = -past; i <= future; i++) {
    const d = new Date(Date.UTC(y, mo - 1 + i, 1));
    const yy = d.getUTCFullYear();
    const mm = String(d.getUTCMonth() + 1).padStart(2, "0");
    out.push(`${yy}-${mm}`);
  }
  return out;
}
